import { useState, useEffect, useCallback } from 'react';
import { CustomerResponse, CreateCustomerRequest } from '../types';
import { SupabaseCustomerRepository } from '../repositories/SupabaseCustomerRepository';
import { CreateCustomerUseCase } from '../use-cases/CreateCustomerUseCase';
import { GetAllCustomersUseCase } from '../use-cases/GetAllCustomersUseCase';
import { UpdateCustomerUseCase, UpdateCustomerRequest } from '../use-cases/UpdateCustomerUseCase';
import { DeleteCustomerUseCase } from '../use-cases/DeleteCustomerUseCase';

// Hook for providing customer data and operations to React components
export interface UseCustomersReturn {
  customers: CustomerResponse[];
  loading: boolean;
  error: string | null;
  createCustomer: (data: CreateCustomerRequest) => Promise<void>;
  updateCustomer: (data: UpdateCustomerRequest) => Promise<void>;
  deleteCustomer: (id: string) => Promise<void>;
  refreshCustomers: () => Promise<void>;
}

// Create repository and use cases (singleton pattern for efficiency)
const customerRepository = new SupabaseCustomerRepository();
const createCustomerUseCase = new CreateCustomerUseCase(customerRepository);
const getAllCustomersUseCase = new GetAllCustomersUseCase(customerRepository);
const updateCustomerUseCase = new UpdateCustomerUseCase(customerRepository);
const deleteCustomerUseCase = new DeleteCustomerUseCase(customerRepository);

export function useCustomers(): UseCustomersReturn {
  const [customers, setCustomers] = useState<CustomerResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshCustomers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getAllCustomersUseCase.execute({});
      setCustomers(result.customers);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch customers';
      setError(errorMessage);
      console.error('Failed to fetch customers:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load customers on mount
  useEffect(() => {
    let isMounted = true;

    const loadCustomers = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await getAllCustomersUseCase.execute({});
        if (isMounted) {
          setCustomers(result.customers);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch customers';
        console.error('Failed to fetch customers:', err);
        if (isMounted) {
          setError(errorMessage);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadCustomers();

    return () => {
      isMounted = false;
    };
  }, []);

  const createCustomer = useCallback(async (data: CreateCustomerRequest) => {
    try {
      setError(null);
      const newCustomer = await createCustomerUseCase.execute(data);
      setCustomers(prev => [newCustomer, ...prev]);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create customer';
      setError(errorMessage);
      throw err;
    }
  }, []);

  const updateCustomer = useCallback(async (data: UpdateCustomerRequest) => {
    try {
      setError(null);
      const updatedCustomer = await updateCustomerUseCase.execute(data);
      setCustomers(prev => 
        prev.map(customer => 
          customer.id === data.id ? updatedCustomer : customer
        )
      );
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update customer';
      setError(errorMessage);
      throw err;
    }
  }, []);

  const deleteCustomer = useCallback(async (id: string) => {
    try {
      setError(null);
      await deleteCustomerUseCase.execute({ id });
      setCustomers(prev => prev.filter(customer => customer.id !== id));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete customer';
      setError(errorMessage);
      throw err;
    }
  }, []);

  // Load customers on mount
  useEffect(() => {
    refreshCustomers();
  }, [refreshCustomers]);

  // Note: Real-time subscription removed - customer profile updates are not urgent.
  // Data is refetched on component mount and when needed.

  return {
    customers,
    loading,
    error,
    createCustomer,
    updateCustomer,
    deleteCustomer,
    refreshCustomers
  };
}
